<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';

import type { Language } from '@/types/locale';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { convertUrlToResizer } from '@/utils/utils.js';

const props = defineProps<{
  images: Array<{ credit: string | null; lang: Language; name: string; url: string }>;
}>();

const globalConfigurationStore = useGlobalConfigurationStore();
const { currentLocale } = storeToRefs(globalConfigurationStore);

const currentIndex = ref(0);
const carouselElement = ref<HTMLElement>();

const touchStartX = ref(0);
const touchEndX = ref(0);
const minSwipeDistance = 50; // Minimum distance in pixels to register as a swipe

const filteredActivityImages = computed(() => {
  if (!props.images?.length)
    return [];
  // DesktopGallery component use a list of 2 to 5 images.
  let images = props.images.filter((image: any) => image.lang === currentLocale.value || !image.lang).slice(0, 6);
  if (images.length === 1) {
    images = [...images, images[0]];
  }
  return images;
});
const totalImages = computed(() => filteredActivityImages.value?.length);

function goToSlide(index: number) {
  if (index >= 0 && index < totalImages.value) {
    currentIndex.value = index;
  }
}

function nextSlide() {
  currentIndex.value = Math.min(currentIndex.value + 1, totalImages.value - 1);
}

function prevSlide() {
  currentIndex.value = Math.max(currentIndex.value - 1, 0);
}

function handleTouchStart(event: TouchEvent) {
  touchStartX.value = event.changedTouches[0].clientX;
  touchEndX.value = touchStartX.value; // Reset endX on new touch start
}

function handleTouchMove(event: TouchEvent) {
  touchEndX.value = event.changedTouches[0].clientX;
}

function handleTouchEnd() {
  const deltaX = touchEndX.value - touchStartX.value;

  if (Math.abs(deltaX) > minSwipeDistance) {
    deltaX < 0 ? nextSlide() : prevSlide();
  }

  touchStartX.value = 0;
  touchEndX.value = 0;
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'ArrowLeft') {
    prevSlide();
  }
  else if (event.key === 'ArrowRight') {
    nextSlide();
  }
}

onMounted(() => {
  if (carouselElement.value) {
    carouselElement.value.addEventListener('keydown', handleKeyDown);
  }
});

onUnmounted(() => {
  if (carouselElement.value) {
    carouselElement.value.removeEventListener('keydown', handleKeyDown);
  }
});
</script>

<template>
  <div
    v-if="filteredActivityImages.length > 0" ref="carouselElement" role="button" class="image-carousel" tabindex="0"
    @keydown="handleKeyDown"
  >
    <div
      class="carousel-inner"
      :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
      aria-roledescription="carousel"
      aria-label="photos"
      aria-live="polite"
      @touchstart.passive="handleTouchStart"
      @touchmove.passive="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div
        v-for="(image, index) in filteredActivityImages" :key="index" role="group" class="carousel-slide"
        aria-roledescription="slide" :aria-label="`picture ${currentIndex + 1} of ${totalImages}`"
      >
        <img
          :src="convertUrlToResizer(image.url)" :alt="image.credit || `Slide ${index + 1}`" class="carousel-image"
          :fetchpriority="index === 0 ? 'high' : 'auto'" height="270" width="355" draggable="false"
        >
      </div>
    </div>

    <div class="carousel-dots">
      <button
        v-for="(_, index) in filteredActivityImages"
        :key="index"
        class="dot"
        :class="{ active: index === currentIndex }"
        :aria-label="`Go to slide ${index + 1}`"
        @click="goToSlide(index)"
      />
    </div>
  </div>
  <!--  <div v-else class="carousel-empty"> -->
  <!--    <div class="carousel-slide"> -->
  <!--      <img -->
  <!--        :src="convertUrlToResizer('https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.pn')" -->
  <!--        alt="picture placeholder" -->
  <!--        class="carousel-image" -->
  <!--        fetchpriority="high" -->
  <!--        draggable="false" -->
  <!--      > -->
  <!--    </div> -->
  <!--  </div> -->
</template>

<style scoped>
.image-carousel {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 800px;
  margin: auto;
  overflow: hidden;
  border-radius: var(--yb-card-radius);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  outline: none;
  -webkit-user-select: none; /* Disable text selection during swipe */
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  touch-action: pan-y; /* Allows vertical scroll, handles horizontal pan via JS */
}

.carousel-inner {
  display: flex;
  transition: transform 0.5s ease-in-out;
  /* touch-action: none; */ /* If you want to completely disable browser default pan/zoom */
}

.carousel-slide {
  flex: 0 0 100%; /* Each slide takes full width */
  width: 100%;
  box-sizing: border-box;
}

.carousel-image {
  display: block; /* Removes bottom space under image */
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: cover;
  pointer-events: none; /* Prevents dragging the image itself */
}

.carousel-dots {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 10;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.dot:hover {
  background-color: rgba(255, 255, 255, 0.8);
}

.dot.active {
  background-color: white;
  border-color: rgba(0, 0, 0, 0.4);
}

.carousel-empty {
  padding: 20px;
  text-align: center;
  color: #666;
  border: 1px dashed #ccc;
  border-radius: 8px;
}
</style>
